import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
} from '@mui/material';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useDeviceToken from '../../../hooks/useDeviceToken';
import useHttp from '../../../hooks/useHttp';
import FunZoneLogo from './FunZoneLogo';

const DeviceConfirmation = ({ allowedDeviceTypes, onDeviceConfirmed }) => {
  const [selectedDeviceType, setSelectedDeviceType] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const {
    createDeviceToken,
    loginWithDeviceToken,
    isLoading: deviceTokenLoading,
    error: apiError,
  } = useDeviceToken();
  const { isLoading: httpLoading, error: httpError } = useHttp();
  const [performingLogin, setPerformingLogin] = useState(false);

  const isLoading = deviceTokenLoading || httpLoading || performingLogin;

  const deviceTypeLabels = {
    company: 'Firmengerät',
    counter: 'Kassensystem',
    private: 'Privates Gerät',
  };

  const handleDeviceConfirmation = async () => {
    if (!selectedDeviceType) {
      setError('Bitte wähle einen Gerätetyp aus.');
      return;
    }
    setPerformingLogin(true);

    setError('');

    try {
      if (selectedDeviceType === 'private') {
        localStorage.setItem('isPrivateDevice', 'true');

        if (onDeviceConfirmed) {
          onDeviceConfirmed(null); // null indicates private device
        }
        navigate('/welcome');
        return;
      }

      // Create device token for non-private devices
      const deviceTokenResult = await createDeviceToken(selectedDeviceType);

      // Complete login using temp auth token + selected device token
      const loginResult = await loginWithDeviceToken(deviceTokenResult.deviceToken);

      if (onDeviceConfirmed) {
        onDeviceConfirmed(
          deviceTokenResult.deviceToken,
          loginResult.token,
          loginResult.intermediateToken
        );
      }

      navigate('/welcome');
    } catch (err) {
      setError(err.message);
    } finally {
      setPerformingLogin(false);
    }
  };

  return (
    <Grid
      container
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '600px',
        width: '95%',
      }}
      rowSpacing={2}
    >
      {/* LOGO */}
      <Grid item xs={12} style={{ marginBottom: '20px' }}>
        <FunZoneLogo />
      </Grid>

      {/* TITLE */}
      <Grid item xs={12}>
        <Typography variant='h5' component='h2' align='center' sx={{ mb: 2 }}>
          Gerät bestätigen
        </Typography>
      </Grid>

      {/* DESCRIPTION */}
      <Grid item xs={12}>
        <Typography variant='body1' align='center' sx={{ mb: 3, color: 'text.secondary' }}>
          Wähle den Typ dieses Geräts aus. Dies stellt sicher, dass nur autorisierte Geräte für
          bestimmte Funktionen verwendet werden können. Zum Beispiel können Kassen-Systeme nur von
          registrierten Kassen-PCs aus bedient werden.
        </Typography>
      </Grid>

      {/* DEVICE TYPE SELECTION */}
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>Gerätetyp auswählen</InputLabel>
          <Select
            value={selectedDeviceType}
            onChange={e => setSelectedDeviceType(e.target.value)}
            disabled={isLoading}
            label='Gerätetyp auswählen'
          >
            {allowedDeviceTypes.map(deviceType => (
              <MenuItem key={deviceType} value={deviceType}>
                {deviceTypeLabels[deviceType] || deviceType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* ERROR ALERT */}
      {(error || apiError || httpError) && (
        <Grid item xs={12}>
          <Alert severity='error'>{error || apiError || httpError}</Alert>
        </Grid>
      )}

      {/* BUTTONS */}
      <Grid item xs={12} sx={{ marginTop: '20px' }} container>
        {/* CONFIRM BUTTON */}
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            type='button'
            onClick={handleDeviceConfirmation}
            disabled={!selectedDeviceType || isLoading}
            sx={{ maxWidth: '200px' }}
          >
            {isLoading ? 'Wird bestätigt...' : 'Bestätigen'}
            <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '5px' }} />
          </Button>
        </Grid>
      </Grid>

      {/* SKIP BUTTON */}
      <Grid item xs={12} sx={{ marginTop: '10px', textAlign: 'center' }}>
        <Button
          type='button'
          variant='text'
          size='small'
          onClick={() => {
            if (onDeviceConfirmed) {
              onDeviceConfirmed(null); // null indicates private device
            }
            navigate('/welcome');
          }}
          disabled={isLoading}
          sx={{ color: 'text.secondary' }}
        >
          Überspringen (privates Gerät)
        </Button>
      </Grid>
    </Grid>
  );
};

export default DeviceConfirmation;
